/**
 * File: contractFormat.ts
 * Path: packages/config/src/contracts/contractFormat.ts
 * Description: Money, date and JSON formatting for contracts.
 *
 * Written by hand rather than with Intl: the same text is built in the
 * browser (admin preview, signing page) and on the server (the PDF and the
 * hash), and Intl output differs between ICU builds ("R 1 000,00" in one
 * runtime, "R1,000.00" in another). A hashed document must read identically
 * everywhere it is built.
 */

/** "R12,500.00" */
export const formatRand = (amount: number): string => {
  const safe = Number.isFinite(amount) ? amount : 0;
  const negative = safe < 0;
  const cents = Math.round(Math.abs(safe) * 100);
  const whole = Math.floor(cents / 100).toString();
  const fraction = (cents % 100).toString().padStart(2, "0");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}R${grouped}.${fraction}`;
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isIsoDate = (value: unknown): value is string =>
  typeof value === "string" && ISO_DATE.test(value);

/** "2026-10-01" → "1 October 2026". Anything else is returned unchanged. */
export const formatContractDate = (iso: string): string => {
  const match = ISO_DATE.exec(iso);
  if (!match) return iso;
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month} ${match[1]}` : iso;
};

/** Epoch ms → "1 October 2026, 14:05 SAST". South African time, always. */
export const formatSastDateTime = (ms: number): string => {
  // SAST is UTC+2 with no daylight saving, so a fixed offset is exact.
  const d = new Date(ms + 2 * 60 * 60 * 1000);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())} SAST`;
};

/** Epoch ms → "2026-10-01" in South African time. */
export const sastIsoDate = (ms: number): string =>
  new Date(ms + 2 * 60 * 60 * 1000).toISOString().slice(0, 10);

/** Number words for the "fourteen (14)" style the templates use. */
const WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
  "twenty",
];
const TENS: Record<number, string> = {
  2: "twenty",
  3: "thirty",
  4: "forty",
  5: "fifty",
  6: "sixty",
};

/** 14 → "fourteen (14)"; past sixty-nine just the figure. */
export const countWords = (n: number): string => {
  const whole = Math.max(0, Math.round(n));
  let words: string | undefined;
  if (whole <= 20) words = WORDS[whole];
  else if (whole < 70) {
    const tens = TENS[Math.floor(whole / 10)];
    const ones = whole % 10;
    words = ones === 0 ? tens : `${tens}-${WORDS[ones]}`;
  }
  return words ? `${words} (${whole})` : String(whole);
};

/**
 * JSON with object keys sorted at every level, so the same value always gives
 * the same string (and so the same hash) whatever order it was built in.
 */
export const canonicalJson = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value === undefined ? null : value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((v) => canonicalJson(v)).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
};
